/**
 * FileLightbox.tsx — Preview modal for images and PDFs.
 * Fetches full-size content via authenticated API calls.
 */
import { useEffect, useState } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { fetchFileViewUrl } from '@/services/patient-files.service';

interface FileLightboxProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  fileName: string;
  folder?: string;
  source?: string;
  isImage: boolean;
  mimeType: string | null;
  onDownload: () => void;
}

export function FileLightbox({ open, onClose, patientId, fileName, folder, source, isImage, mimeType, onDownload }: FileLightboxProps) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setFileUrl(null); return; }
    let cancelled = false;
    setLoading(true);
    fetchFileViewUrl(patientId, fileName, folder, source).then((url) => {
      if (!cancelled) { setFileUrl(url); setLoading(false); }
    });
    return () => { cancelled = true; if (fileUrl) URL.revokeObjectURL(fileUrl); };
  }, [open, patientId, fileName, folder, source]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
  }, [open, onClose]);

  if (!open) return null;

  const isPdf = mimeType === 'application/pdf';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 flex-shrink-0">
          <h3 className="text-sm font-semibold text-slate-900 truncate">{fileName}</h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={onDownload}>
              <Download className="h-3.5 w-3.5 mr-1" />
              Download
            </Button>
            <button type="button" onClick={onClose} className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-4 min-h-[300px]">
          {loading ? (
            <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
          ) : fileUrl && isImage ? (
            <img src={fileUrl} alt={fileName} className="max-w-full max-h-[75vh] object-contain rounded shadow-lg" />
          ) : fileUrl && isPdf ? (
            <iframe src={fileUrl} title={fileName} className="w-full h-[75vh] rounded border border-slate-200" />
          ) : (
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-500">Preview not available for this file type.</p>
              <Button onClick={onDownload}>
                <Download className="h-4 w-4 mr-1" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
